import DashboardLayout from '@/components/DashboardLayout'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ImageUpload } from '@/components/ui/image-upload'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { lazy, Suspense, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import { ArrowLeft, MapPin, Loader2 } from 'lucide-react'
import axios from 'axios'

// lazy porque o resolver do Inertia usa import.meta.glob eager: sem isto, o
// Leaflet entraria no bundle que TODA página autenticada baixa.
const LocationPicker = lazy(() => import('@/components/school/LocationPicker'))

interface School {
  id: number
  name: string
  description: string
  phone: string
  avatar_url: string | null
  address?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  latitude?: number
  longitude?: number
  checkin_radius_meters?: number
  timezone?: string
  has_location?: boolean
  full_address?: string
  slug?: string | null
  legal_name?: string | null
  cnpj?: string | null
  privacy_contact_email?: string | null
}

interface EditProps {
  school: School
}

const schoolSchema = z.object({
  name: z.string().min(3, 'Nome deve ter no mínimo 3 caracteres'),
  description: z.string().optional(),
  phone: z
    .string()
    .min(8, 'Telefone é obrigatório')
    .regex(/^[+]?[0-9\s\-\(\)]+$/, 'Telefone inválido'),
  legal_name: z.string().optional(),
  cnpj: z
    .string()
    .optional()
    .refine(
      (v) => !v || v.replace(/\D/g, '').length === 14,
      'CNPJ deve ter 14 dígitos',
    ),
  privacy_contact_email: z
    .string()
    .optional()
    .refine(
      (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
      'E-mail inválido',
    ),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postal_code: z.string().optional(),
  country: z.string().optional(),
  checkin_radius_meters: z.number().min(10).max(500).optional(),
  timezone: z.string().optional(),
})

function formatCnpj(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14)
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
}

type SchoolFormData = z.infer<typeof schoolSchema>

export default function Edit({ school }: EditProps) {
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [isGeocoding, setIsGeocoding] = useState(false)
  const [isLoadingCep, setIsLoadingCep] = useState(false)
  const [showManualCoords, setShowManualCoords] = useState(false)
  const [manualLat, setManualLat] = useState<string>('')
  const [manualLng, setManualLng] = useState<string>('')
  const [coordinates, setCoordinates] = useState<{
    lat: number
    lng: number
  } | null>(
    school.latitude && school.longitude
      ? {
          lat: Number(school.latitude),
          lng: Number(school.longitude),
        }
      : null,
  )

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SchoolFormData>({
    resolver: zodResolver(schoolSchema),
    defaultValues: {
      name: school.name,
      description: school.description || '',
      phone: school.phone,
      legal_name: school.legal_name || '',
      cnpj: school.cnpj ? formatCnpj(school.cnpj) : '',
      privacy_contact_email: school.privacy_contact_email || '',
      address: school.address || '',
      city: school.city || '',
      state: school.state || '',
      postal_code: school.postal_code || '',
      country: school.country || 'Brasil',
      checkin_radius_meters: school.checkin_radius_meters || 100,
      timezone: school.timezone || 'America/Fortaleza',
    },
  })

  const addressFields = watch(['address', 'city', 'state', 'postal_code'])
  const hasAddressData = addressFields.some(
    (field) => field && field.length > 0,
  )
  const postalCode = watch('postal_code')

  const handleCepSearch = async () => {
    if (!postalCode || postalCode.length < 8) {
      toast.error('Digite um CEP válido (8 dígitos)')
      return
    }

    const cleanCep = postalCode.replace(/\D/g, '')
    if (cleanCep.length !== 8) {
      toast.error('CEP deve ter 8 dígitos')
      return
    }

    setIsLoadingCep(true)
    try {
      const response = await axios.get(
        `https://viacep.com.br/ws/${cleanCep}/json/`,
      )

      if (response.data.erro) {
        toast.error('CEP não encontrado')
        return
      }

      // Preenche os campos automaticamente
      setValue('address', response.data.logradouro || '')
      setValue('city', response.data.localidade || '')
      setValue('state', response.data.uf || '')
      setValue('country', 'Brasil')

      toast.success('Endereço encontrado! Agora clique em "Obter Coordenadas"')
    } catch (error) {
      toast.error('Erro ao buscar CEP. Verifique se o CEP está correto.')
    } finally {
      setIsLoadingCep(false)
    }
  }

  const handleGeocode = async () => {
    setIsGeocoding(true)
    try {
      // Pega os valores atuais do formulário
      const formValues = watch()

      // Pega o CSRF token
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content')

      // Envia os dados do formulário para o geocoding
      const response = await axios.post(
        '/school/geocode',
        {
          address: formValues.address,
          city: formValues.city,
          state: formValues.state,
          postal_code: formValues.postal_code,
          country: formValues.country || 'Brasil',
        },
        {
          headers: {
            'X-CSRF-Token': csrfToken,
          },
        },
      )

      if (response.data.success) {
        setCoordinates({
          lat: Number(response.data.latitude),
          lng: Number(response.data.longitude),
        })
        toast.success(
          `Coordenadas obtidas! (${response.data.display_name || 'Localização encontrada'})`,
        )
        setShowManualCoords(false)
      } else {
        toast.error(
          response.data.error || 'Não foi possível obter as coordenadas',
        )
      }
    } catch (error: any) {
      toast.error(
        error.response?.data?.error ||
          'Erro ao buscar coordenadas. Tente inserir manualmente.',
      )
    } finally {
      setIsGeocoding(false)
    }
  }

  const handleManualCoords = () => {
    const lat = parseFloat(manualLat)
    const lng = parseFloat(manualLng)

    if (isNaN(lat) || isNaN(lng)) {
      toast.error('Coordenadas inválidas')
      return
    }

    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      toast.error('Coordenadas fora do intervalo válido')
      return
    }

    setCoordinates({ lat, lng })
    toast.success('Coordenadas configuradas manualmente!')
    setShowManualCoords(false)
  }

  const onSubmit = async (data: SchoolFormData) => {
    const formData = new FormData()
    formData.append('school[name]', data.name)
    formData.append('school[description]', data.description || '')
    formData.append('school[phone]', data.phone)
    formData.append('school[legal_name]', data.legal_name || '')
    formData.append('school[cnpj]', (data.cnpj || '').replace(/\D/g, ''))
    formData.append(
      'school[privacy_contact_email]',
      data.privacy_contact_email || '',
    )
    formData.append('school[address]', data.address || '')
    formData.append('school[city]', data.city || '')
    formData.append('school[state]', data.state || '')
    formData.append('school[postal_code]', data.postal_code || '')
    formData.append('school[country]', data.country || 'Brasil')
    formData.append(
      'school[checkin_radius_meters]',
      String(data.checkin_radius_meters || 100),
    )
    formData.append('school[timezone]', data.timezone || 'America/Fortaleza')

    // Inclui coordenadas se foram obtidas
    if (coordinates) {
      formData.append('school[latitude]', String(coordinates.lat))
      formData.append('school[longitude]', String(coordinates.lng))
    }

    if (avatarFile) {
      formData.append('school[avatar]', avatarFile)
    }

    router.visit('/school/profile', {
      method: 'patch',
      data: formData,
      forceFormData: true,
      onSuccess: () => {
        toast.success('Perfil atualizado com sucesso!')
      },
      onError: (errors) => {
        const errorMessage = Object.values(errors).flat().join(', ')
        toast.error(errorMessage || 'Erro ao atualizar perfil')
      },
    })
  }

  return (
    <DashboardLayout schoolName={school.name}>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => router.visit('/school/profile')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Editar Perfil</h2>
            <p className="text-muted-foreground">
              Atualize as informações da sua escola
            </p>
          </div>
        </div>

        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>Informações da Escola</CardTitle>
            <CardDescription>
              Preencha os dados abaixo para atualizar o perfil
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label>Logo/Avatar da Escola</Label>
                <ImageUpload
                  currentImageUrl={school.avatar_url}
                  onImageSelect={setAvatarFile}
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Nome da Escola *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Escola de Surf Onda Perfeita"
                  {...register('name')}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">
                    {errors.name.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  placeholder="A melhor escola de surf da cidade..."
                  rows={4}
                  {...register('description')}
                />
                {errors.description && (
                  <p className="text-sm text-destructive">
                    {errors.description.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Telefone *</Label>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => <PhoneInput {...field} id="phone" />}
                />
                {errors.phone && (
                  <p className="text-sm text-destructive">
                    {errors.phone.message}
                  </p>
                )}
              </div>

              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold mb-1">
                  Dados legais (LGPD)
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Esses dados aparecem na Política de Privacidade pública da
                  escola, usada no Google Play Console.
                </p>

                {school.slug && (
                  <div className="mb-4 p-3 bg-muted/50 rounded-md text-sm">
                    <div className="font-medium mb-1">URLs públicas</div>
                    <div className="space-y-1 text-xs font-mono break-all">
                      <div>
                        <span className="text-muted-foreground">Política:</span>{' '}
                        <a
                          href={`/${school.slug}/policy-privacy`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          /{school.slug}/policy-privacy
                        </a>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Termos:</span>{' '}
                        <a
                          href={`/${school.slug}/terms`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          /{school.slug}/terms
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {!school.slug && (
                  <div className="mb-4 p-3 border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 rounded-md text-sm text-amber-900 dark:text-amber-200">
                    Esta escola ainda não tem um <strong>slug</strong> (URL
                    pública). Peça ao suporte do Bee Wave para configurar.
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="legal_name">Razão social</Label>
                    <Input
                      id="legal_name"
                      type="text"
                      placeholder="Escola de Surf Onda Perfeita LTDA"
                      {...register('legal_name')}
                    />
                    <p className="text-xs text-muted-foreground">
                      Nome jurídico completo (como no contrato social).
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cnpj">CNPJ</Label>
                    <Controller
                      name="cnpj"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="cnpj"
                          type="text"
                          placeholder="00.000.000/0000-00"
                          value={field.value || ''}
                          onChange={(e) =>
                            field.onChange(formatCnpj(e.target.value))
                          }
                          maxLength={18}
                        />
                      )}
                    />
                    {errors.cnpj && (
                      <p className="text-sm text-destructive">
                        {errors.cnpj.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="privacy_contact_email">
                      E-mail de privacidade (LGPD)
                    </Label>
                    <Input
                      id="privacy_contact_email"
                      type="email"
                      placeholder="dpo@escola.com.br"
                      {...register('privacy_contact_email')}
                    />
                    {errors.privacy_contact_email && (
                      <p className="text-sm text-destructive">
                        {errors.privacy_contact_email.message}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Canal para titulares exercerem direitos da LGPD.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-6 mt-6">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-semibold">
                    Localização da Escola
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Configure o endereço para habilitar check-in por
                  geolocalização no app mobile
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="postal_code">CEP</Label>
                    <div className="flex gap-2">
                      <Input
                        id="postal_code"
                        type="text"
                        placeholder="60000-000"
                        {...register('postal_code')}
                        maxLength={9}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCepSearch}
                        disabled={isLoadingCep || !postalCode}
                      >
                        {isLoadingCep ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Buscando...
                          </>
                        ) : (
                          'Buscar CEP'
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Digite o CEP e clique em "Buscar CEP" para preencher
                      automaticamente
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="state">Estado</Label>
                    <Input
                      id="state"
                      type="text"
                      placeholder="CE"
                      maxLength={2}
                      {...register('state')}
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="address">Endereço (Rua e número)</Label>
                    <Input
                      id="address"
                      type="text"
                      placeholder="Av. Beira Mar, 1234"
                      {...register('address')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">Cidade</Label>
                    <Input
                      id="city"
                      type="text"
                      placeholder="Fortaleza"
                      {...register('city')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="country">País</Label>
                    <Input id="country" type="text" {...register('country')} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="checkin_radius_meters">
                      Raio de Check-in (metros)
                    </Label>
                    <Input
                      id="checkin_radius_meters"
                      type="number"
                      min={10}
                      max={500}
                      {...register('checkin_radius_meters', {
                        valueAsNumber: true,
                      })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Distância máxima permitida para check-in (padrão: 100
                      metros)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timezone">Fuso Horário</Label>
                    <Controller
                      name="timezone"
                      control={control}
                      render={({ field }) => (
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o fuso horário" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="America/Fortaleza">
                              Fortaleza (UTC-3)
                            </SelectItem>
                            <SelectItem value="America/Sao_Paulo">
                              São Paulo (UTC-3)
                            </SelectItem>
                            <SelectItem value="America/Manaus">
                              Manaus (UTC-4)
                            </SelectItem>
                            <SelectItem value="America/Rio_Branco">
                              Rio Branco (UTC-5)
                            </SelectItem>
                            <SelectItem value="America/Noronha">
                              Fernando de Noronha (UTC-2)
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                    <p className="text-xs text-muted-foreground">
                      Fuso horário usado para validação de check-in
                    </p>
                  </div>
                </div>

                {hasAddressData && (
                  <div className="mt-4 space-y-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleGeocode}
                      disabled={isGeocoding}
                      className="w-full"
                    >
                      {isGeocoding ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Obtendo Coordenadas...
                        </>
                      ) : (
                        <>
                          <MapPin className="mr-2 h-4 w-4" />
                          Obter Coordenadas Automaticamente
                        </>
                      )}
                    </Button>

                    <div className="text-center">
                      <Button
                        type="button"
                        variant="link"
                        onClick={() => setShowManualCoords(!showManualCoords)}
                        className="text-xs"
                      >
                        {showManualCoords
                          ? 'Ocultar'
                          : 'Ou inserir coordenadas manualmente'}
                      </Button>
                    </div>

                    {showManualCoords && (
                      <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                        <p className="text-sm text-muted-foreground">
                          <strong>Dica:</strong> Para obter coordenadas exatas,
                          acesse{' '}
                          <a
                            href="https://www.google.com/maps"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            Google Maps
                          </a>
                          , clique com o botão direito no local e copie as
                          coordenadas.
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label htmlFor="manual-lat">Latitude</Label>
                            <Input
                              id="manual-lat"
                              type="text"
                              placeholder="-3.731862"
                              value={manualLat}
                              onChange={(e) => setManualLat(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="manual-lng">Longitude</Label>
                            <Input
                              id="manual-lng"
                              type="text"
                              placeholder="-38.526670"
                              value={manualLng}
                              onChange={(e) => setManualLng(e.target.value)}
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={handleManualCoords}
                          className="w-full"
                        >
                          Aplicar Coordenadas
                        </Button>
                      </div>
                    )}

                    <Suspense
                      fallback={
                        <div className="h-72 w-full animate-pulse rounded-lg border bg-muted" />
                      }
                    >
                      <LocationPicker
                        value={coordinates}
                        onChange={setCoordinates}
                      />
                    </Suspense>

                    {coordinates && (
                      <div className="p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg space-y-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm font-semibold text-green-800 dark:text-green-200">
                              ✓ Coordenadas Configuradas
                            </p>
                            <p className="text-xs text-green-600 dark:text-green-400 mt-1 font-mono">
                              Lat: {coordinates.lat.toFixed(6)}, Lng:{' '}
                              {coordinates.lng.toFixed(6)}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setCoordinates(null)}
                            className="text-green-700 hover:text-green-900 dark:text-green-300"
                          >
                            Remover
                          </Button>
                        </div>
                        <a
                          href={`https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-green-700 dark:text-green-400 hover:underline flex items-center gap-1"
                        >
                          Ver no Google Maps →
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => router.visit('/school/profile')}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
